import {
	Button,
	ButtonBase,
	Collapse,
	Divider,
	Stack,
	Switch,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { ExpenseCard } from "web_myplann/Finances/Components/ExpenseCard";
import { ExpensePicker } from "web_myplann/Finances/Components/ExpensePicker";
import { FooterButton } from "web_myplann/Finances/Components/FooterButton";
import { useExpenseForm } from "web_myplann/Finances/Forms/useExpenseForm";
import { ClientFinancialPlanExpenseTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IncrementalCounter } from "web_myplann/Shared/Components/IncrementalCounter";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

export const ExpensesPage = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();

	const {
		onSubmit,
		setValue,
		control,
		expensesRemove,
		expensesAppend,
		expensesFields,
	} = useExpenseForm();
	const exact = useWatch({ control, name: "exact" });
	const totalValue = useWatch({ control, name: "totalValue" });

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [drawerKey, setDrawerKey] =
		useState<ClientFinancialPlanExpenseTypeEnum | null>(null);
	const [expanded, setExpanded] = useState(false);
	const [previousFieldsLength, setPreviousFieldsLength] = useState(
		expensesFields.length,
	);

	const [previousExact, setPreviousExact] = useState(exact);

	useEffect(() => {
		if (exact && expensesFields.length === 0 && previousFieldsLength > 0) {
			setValue("exact", false);
		}
		setPreviousFieldsLength(expensesFields.length);
	}, [expensesFields.length, exact, setValue, previousFieldsLength]);

	useEffect(() => {
		if (exact && !previousExact) {
			const sum = expensesFields.reduce(
				(acc, field) => acc + (field.monthlyPayment ?? 0),
				0,
			);
			setValue("totalValue", sum);
		}
		setPreviousExact(exact);
	}, [exact, previousExact, expensesFields, setValue]);

	const handleClick = (type: ClientFinancialPlanExpenseTypeEnum) => {
		setDrawerOpen(true);
		setDrawerKey(type);
	};

	const expenseTypes = Object.values(
		ClientFinancialPlanExpenseTypeEnum,
	) as ClientFinancialPlanExpenseTypeEnum[];

	const handleNavigate = async () => {
		await onSubmit();
		navigate(Routing.Finances.Overview.path(), {
			replace: true,
			state: { from: location.state?.from },
		});
	};

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Finances.title)}
					defaultRoute={Routing.Dashboard.path()}
				/>
			}
		>
			<Stack py={1.6} gap={0.6} px={1.6}>
				<Typography variant="h3" color={theme.palette.grey[900]}>
					{t(Resources.Finances.Expenses.titleTentatively)}
				</Typography>
				<Typography variant="body1" color={theme.palette.grey[500]}>
					{t(Resources.Finances.Expenses.descriptionTentative)}
				</Typography>
				<IncrementalCounter
					defaultValue={useWatch({ control, name: "totalValue" }) ?? 0}
					setValue={(value) => {
						setValue("totalValue", value);
					}}
					disabled={exact}
				/>
			</Stack>

			<Stack py={1.6} px={1.6}>
				<Divider sx={{ borderColor: theme.palette.grey[200] }} />
			</Stack>

			<Stack
				px={1.6}
				direction="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<Typography variant="h3" color={theme.palette.grey[900]}>
					{t(Resources.Finances.Expenses.titleExact)}
				</Typography>
				<Switch
					sx={{
						transform: "scale(0.75)",
					}}
					checked={exact}
					onChange={() => setValue("exact", !exact)}
				/>
			</Stack>

			{!exact && (
				<>
					<Stack px={1.6} pt={0.8}>
						<Typography variant="body1" color={theme.palette.grey[500]}>
							{t(Resources.Finances.Expenses.descriptionExact)}
						</Typography>
					</Stack>

					<Stack px={1.6} pt={0.8}>
						<Typography
							fontSize={14}
							fontWeight={600}
							lineHeight={2.4}
							color={theme.palette.grey[700]}
						>
							{t(Resources.Finances.Expenses.subtitleExact)}
						</Typography>

						<Typography
							fontSize={14}
							fontWeight={400}
							color={theme.palette.grey[500]}
							sx={
								expanded
									? {}
									: {
											overflow: "hidden",
											display: "-webkit-box",
											WebkitLineClamp: 4,
											WebkitBoxOrient: "vertical",
										}
							}
						>
							{t(Resources.Finances.Expenses.whatToCountDescription)}
						</Typography>

						<Stack
							pb={1.2}
							pt={0.4}
							onClick={() => setExpanded((prev) => !prev)}
						>
							<ButtonBase
								sx={{
									alignSelf: "flex-start",
								}}
							>
								<Typography variant="body2" color={theme.palette.primary.main}>
									{expanded
										? t(Resources.Finances.Income.readLess)
										: t(Resources.Finances.Income.readMore)}
								</Typography>
							</ButtonBase>
						</Stack>
					</Stack>
				</>
			)}

			<Collapse in={exact} timeout={1000} unmountOnExit>
				{expenseTypes.map((type, i) => {
					const withBackground = i % 2 !== 0;

					return (
						<ExpenseCard
							key={type}
							type={type}
							onClick={() => handleClick(type)}
							withBackground={withBackground}
							control={control}
							setValue={setValue}
							expensesRemove={expensesRemove}
						/>
					);
				})}
			</Collapse>

			<Stack px={1.6} py={1.6}>
				{exact ? (
					<Button disabled={totalValue === 0} onClick={handleNavigate}>
						{t(Resources.Common.continue)}
					</Button>
				) : (
					<FooterButton disabled={totalValue === 0} onClick={handleNavigate} />
				)}
			</Stack>

			{drawerKey && (
				<ExpensePicker
					open={drawerOpen}
					onClose={() => setDrawerOpen(false)}
					expenseKey={drawerKey}
					control={control}
					expensesAppend={expensesAppend}
					expensesRemove={expensesRemove}
				/>
			)}
		</MyPlannLayout>
	);
};
